import { useCallback, useEffect, useRef, useState } from "react";
import type { FeedbackCategory } from "./feedbackForm";
import { type PageCaptureState, pageCaptureSlot, SEND_WAIT_MS, settleWithin } from "./pageCaptureRules";

/**
 * The page screenshot taken as the dialog opened (`capture`, from startPageCapture): its state for
 * the Screenshots section, removing it, and the screenshots to send with it.
 */
export function usePageCapture(capture: Promise<File | null>) {
  const [result, setResult] = useState<File | null | undefined>(undefined);
  const [removed, setRemoved] = useState(false);
  // Read when sending, which may happen after the render that last saw it.
  const removedRef = useRef(false);
  useEffect(() => {
    let current = true;
    void capture.then((file) => {
      if (current) setResult(file);
    });
    return () => {
      current = false;
    };
  }, [capture]);

  const remove = useCallback(() => {
    removedRef.current = true;
    setRemoved(true);
  }, []);

  /** `files` to send, with the page screenshot first when it's attached (waiting a little for it). */
  const withPageCapture = useCallback(
    async (category: FeedbackCategory, files: File[]): Promise<File[]> => {
      const file = await settleWithin(capture, SEND_WAIT_MS);
      const slot = pageCaptureSlot({ result: file ?? null, removed: removedRef.current }, category, files.length);
      return slot === "attached" && file ? [file, ...files] : files;
    },
    [capture],
  );

  const state: PageCaptureState = { result, removed };
  return { state, remove, withPageCapture };
}
